/* ripple AIR "First Reactions" player.
   Lazy by design: tiles ship only a poster. A <video> is created for the ACTIVE tile only and
   torn down when it stops being active, so at rest the section costs a handful of JPEGs.
   Nothing plays until the section is genuinely on screen, and nothing keeps playing once it
   leaves - `inView` gates every play path, including the end-of-video chain and the lightbox
   close, which is what stopped stray clips reappearing further down the page. */
(function () {
  'use strict';
  function qsa(el, s) { return [].slice.call(el.querySelectorAll(s)); }
  function fmt(t) {
    if (!isFinite(t) || t < 0) t = 0;
    var m = Math.floor(t / 60), s = Math.floor(t % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }
  function setP(el, r) {
    if (!isFinite(r)) r = 0;
    el.style.setProperty('--p', Math.max(0, Math.min(1, r)));
  }
  var XICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
    'stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  function Rar(root) {
    this.root = root;
    this.rail = root.querySelector('[data-rar-rail]');
    this.tiles = qsa(root, '[data-rar-tile]');
    this.i = 0; this.lb = null; this.inView = false;
    if (this.tiles.length) this.init();
  }

  Rar.prototype.lbOpen = function () {
    return !!(this.lb && this.lb.classList.contains('is-open'));
  };

  Rar.prototype.srcs = function (t) {
    try { return JSON.parse(t.getAttribute('data-rar-src') || '[]'); } catch (e) { return []; }
  };

  Rar.prototype.mount = function (tile, muted) {
    var have = tile.querySelector('video');
    if (have) return have;
    var list = this.srcs(tile);
    if (!list.length) return null;
    var v = document.createElement('video');
    v.muted = muted !== false; v.defaultMuted = v.muted;
    v.playsInline = true; v.setAttribute('playsinline', '');
    v.preload = 'auto';
    var p = tile.getAttribute('data-rar-poster'); if (p) v.poster = p;
    list.forEach(function (s) {
      var el = document.createElement('source');
      el.src = s.src; if (s.type) el.type = s.type;
      v.appendChild(el);
    });
    tile.appendChild(v);
    return v;
  };

  Rar.prototype.unmount = function (tile) {
    var v = tile.querySelector('video');
    if (v) {
      try { v.onended = null; v.pause(); while (v.firstChild) v.removeChild(v.firstChild); v.removeAttribute('src'); v.load(); } catch (e) {}
      if (v.parentNode) v.parentNode.removeChild(v);
    }
    tile.classList.remove('is-playing');
  };

  Rar.prototype.center = function (tile) {
    var r = this.rail;
    if (!r || r.scrollWidth <= r.clientWidth + 1) return;   /* nothing to scroll on desktop */
    var left = tile.offsetLeft - (r.clientWidth - tile.offsetWidth) / 2;
    try { r.scrollTo({ left: Math.max(0, left), behavior: 'smooth' }); }
    catch (e) { r.scrollLeft = Math.max(0, left); }
  };

  /* Arrows only earn their place when a WHOLE further tile is out of view. On desktop the
     five tiles now share the row, so this hides them there automatically. */
  Rar.prototype.syncArrows = function () {
    var rail = this.rail, first = this.tiles[0];
    if (!rail || !first) return;
    var hidden = rail.scrollWidth - rail.clientWidth;
    this.root.classList.toggle('is-noarrows', hidden < first.offsetWidth);
  };

  Rar.prototype.playAt = function (i, scroll) {
    var self = this, n = this.tiles.length;
    this.i = ((i % n) + n) % n;
    this.tiles.forEach(function (t, k) { if (k !== self.i) self.unmount(t); });
    /* never start anything while the section is off screen */
    if (!this.inView || this.lbOpen()) return;
    var tile = this.tiles[this.i];
    var v = this.mount(tile, true);
    if (!v) return;
    tile.classList.add('is-playing');
    v.onended = function () { self.playAt(self.i + 1, true); };
    var p = v.play(); if (p && p.catch) p.catch(function () {});
    if (scroll !== false) this.center(tile);
  };

  Rar.prototype.stop = function () {
    var self = this;
    this.tiles.forEach(function (t) { self.unmount(t); });
  };

  /* ------------------------------ lightbox ------------------------------ */
  Rar.prototype.buildLb = function () {
    if (this.lb) return this.lb;
    var self = this;
    var lb = document.createElement('div');
    lb.className = 'rar__lb';
    /* sound is a plain on/off toggle - no volume slider */
    lb.innerHTML =
      '<button class="rar__lbnav rar__lbnav--prev" aria-label="Previous video">&#10094;</button>' +
      '<div class="rar__stage">' +
        '<button class="rar__x" aria-label="Close">' + XICON + '</button>' +
        '<div class="rar__ctrl">' +
          '<span class="rar__time">0:00</span>' +
          '<input class="rar__seek" type="range" min="0" max="1000" value="0" aria-label="Seek">' +
          '<button class="rar__mute" aria-label="Mute" aria-pressed="false">' +
            '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
            '<path d="M4 9v6h4l5 4V5L8 9H4z"/><path class="rar__wave" d="M16 8.8a4.5 4.5 0 010 6.4" ' +
            'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>' +
      '<button class="rar__lbnav rar__lbnav--next" aria-label="Next video">&#10095;</button>';
    document.body.appendChild(lb);

    var stage = lb.querySelector('.rar__stage');
    var seek = lb.querySelector('.rar__seek');
    var time = lb.querySelector('.rar__time');
    var wave = lb.querySelector('.rar__wave');
    var mute = lb.querySelector('.rar__mute');

    lb.addEventListener('click', function (e) { if (e.target === lb) self.closeLb(); });
    lb.querySelector('.rar__x').addEventListener('click', function () { self.closeLb(); });
    lb.querySelector('.rar__lbnav--prev').addEventListener('click', function () { self.openLb(self.i - 1); });
    lb.querySelector('.rar__lbnav--next').addEventListener('click', function () { self.openLb(self.i + 1); });

    seek.addEventListener('input', function () {
      var v = self.lbVideo;
      setP(seek, seek.value / 1000);
      if (v && isFinite(v.duration)) v.currentTime = (seek.value / 1000) * v.duration;
    });
    mute.addEventListener('click', function () {
      var v = self.lbVideo; if (!v) return;
      v.muted = !v.muted;
      if (!v.muted && !v.volume) v.volume = 1;
    });

    /* swipe between videos */
    var x0 = null;
    stage.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0; x0 = null;
      if (Math.abs(dx) > 45) self.openLb(self.i + (dx < 0 ? 1 : -1));
    }, { passive: true });

    document.addEventListener('keydown', function (e) {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') self.closeLb();
      else if (e.key === 'ArrowRight') self.openLb(self.i + 1);
      else if (e.key === 'ArrowLeft') self.openLb(self.i - 1);
    });

    this.lb = lb; this.lbEls = { stage: stage, seek: seek, time: time, wave: wave, mute: mute };
    return lb;
  };

  Rar.prototype.openLb = function (i) {
    var self = this, n = this.tiles.length;
    i = ((i % n) + n) % n;
    var lb = this.buildLb(), els = this.lbEls;
    this.stop();
    this.i = i;

    if (this.lbVideo && this.lbVideo.parentNode) this.lbVideo.parentNode.removeChild(this.lbVideo);
    var tile = this.tiles[i];
    var v = document.createElement('video');
    v.playsInline = true; v.setAttribute('playsinline', '');
    v.preload = 'auto'; v.controls = false;
    var p = tile.getAttribute('data-rar-poster'); if (p) v.poster = p;
    this.srcs(tile).forEach(function (s) {
      var el = document.createElement('source');
      el.src = s.src; if (s.type) el.type = s.type;
      v.appendChild(el);
    });
    els.stage.insertBefore(v, els.stage.firstChild);
    this.lbVideo = v;

    /* opening is a user gesture, so sound is allowed */
    v.muted = false; v.volume = 1;
    els.seek.value = 0; setP(els.seek, 0);
    els.wave.style.opacity = 1;
    els.mute.setAttribute('aria-pressed', 'false');
    els.time.textContent = '0:00';

    v.addEventListener('loadedmetadata', function () {
      els.time.textContent = '0:00 / ' + fmt(v.duration);
    });
    v.addEventListener('timeupdate', function () {
      if (!isFinite(v.duration) || !v.duration) return;
      var r = v.currentTime / v.duration;
      els.seek.value = Math.round(r * 1000);
      setP(els.seek, r);
      els.time.textContent = fmt(v.currentTime) + ' / ' + fmt(v.duration);
    });
    v.addEventListener('volumechange', function () {
      els.wave.style.opacity = v.muted ? 0 : 1;
      els.mute.setAttribute('aria-pressed', v.muted ? 'true' : 'false');
      els.mute.setAttribute('aria-label', v.muted ? 'Unmute' : 'Mute');
    });
    v.addEventListener('ended', function () { self.openLb(self.i + 1); });

    lb.classList.add('is-open');
    document.body.classList.add('rar-locked');
    var pr = v.play();
    if (pr && pr.catch) pr.catch(function () { v.muted = true; v.play(); });
  };

  Rar.prototype.closeLb = function () {
    if (!this.lb) return;
    this.lb.classList.remove('is-open');
    document.body.classList.remove('rar-locked');
    var v = this.lbVideo;
    if (v) {
      try { v.pause(); while (v.firstChild) v.removeChild(v.firstChild); v.load(); } catch (e) {}
      if (v.parentNode) v.parentNode.removeChild(v);
      this.lbVideo = null;
    }
    /* only pick the row back up if the section is still on screen */
    if (this.inView) this.playAt(this.i, true); else this.stop();
  };

  /* ------------------------------ wiring ------------------------------ */
  Rar.prototype.init = function () {
    var self = this;

    this.tiles.forEach(function (t, i) {
      t.addEventListener('click', function () { self.openLb(i); });
    });
    qsa(this.root, '[data-rar-prev]').forEach(function (b) {
      b.addEventListener('click', function () { self.playAt(self.i - 1, true); });
    });
    qsa(this.root, '[data-rar-next]').forEach(function (b) {
      b.addEventListener('click', function () { self.playAt(self.i + 1, true); });
    });

    this.syncArrows();
    if (window.requestAnimationFrame) requestAnimationFrame(function () { self.syncArrows(); });
    window.addEventListener('load', function () { self.syncArrows(); });
    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t); t = setTimeout(function () { self.syncArrows(); }, 150);
    });

    if (!('IntersectionObserver' in window)) { this.inView = true; this.playAt(0, false); return; }
    /* a third of the section has to be on screen before anything loads or plays, so the
       clips are not already finished by the time you scroll down to them */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        self.inView = e.isIntersecting;
        if (e.isIntersecting) {
          self.syncArrows();
          if (!self.lbOpen()) self.playAt(self.i, false);
        } else if (!self.lbOpen()) {
          self.stop();
        }
      });
    }, { rootMargin: '0px', threshold: 0.33 });
    io.observe(this.root);
  };

  function boot() { qsa(document, '[data-rar]').forEach(function (r) { if (!r.__rar) r.__rar = new Rar(r); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
